'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var dateFns = require('date-fns');
var array = require('./array.js');

/**
 * Add a day to a date.
 *
 * @param date Date
 * @returns Date
 */ const addDay = (date)=>dateFns.addDays(date, 1);
/**
 * Add a month to a date.
 *
 * @param date Date
 * @returns Date
 */ const addMonth = (date)=>dateFns.addMonths(date, 1);
/**
 * Add a week to a date.
 *
 * @param date Date
 * @returns Date
 */ const addWeek = (date)=>dateFns.addWeeks(date, 1);
/**
 * Add a year to a date.
 *
 * @param date Date
 * @returns Date
 */ const addYear = (date)=>dateFns.addYears(date, 1);
/**
 *
 * @param date Date
 * @param other Date
 * @returns boolean
 */ const beforeDate = (date, other)=>dateFns.isBefore(date, other);
/**
 * Convert a maybe date to a date.
 * Returns null if the date is invalid.
 *
 * @param date MaybeDate
 * @returns Date | null
 */ const convertToDate = (date, inputFormat)=>{
    if (!date) return null;
    if (typeof date === 'string' && validIso(date)) {
        const parsedIsoDate = dateFns.parseISO(date);
        if (validDate(parsedIsoDate)) {
            return parsedIsoDate;
        }
    }
    if (typeof date === 'string' && validDate(date, inputFormat)) {
        return new Date(date);
    }
    if (typeof date === 'number' && validDate(date)) {
        return new Date(date);
    }
    if (date instanceof Date && validDate(date)) {
        return date;
    }
    return null;
};
/**
 * Get the end of the week for a date
 *
 * @param date Date
 * @returns Date
 */ const endWeek = (date)=>dateFns.endOfWeek(date);
/**
 * Format a date
 *
 * @param date Date
 * @param _format string
 * @param locale Locale
 * @returns Date
 */ const formatDate = (date, _format, locale)=>dateFns.format(date, _format, {
        locale
    });
/**
 * Generate a calendar of 7 days in a week.
 * Null represents the days that are not in the current month.
 *
 * @param date Date
 * @returns (Date | null)[][]
 */ const generateCalendar = (date)=>{
    const daysInMonth = dateFns.getDaysInMonth(date);
    const startDateOfCurrentMonth = dateFns.startOfMonth(date);
    const startWeekday = dateFns.getDay(startDateOfCurrentMonth);
    const endDateOfCurrentMonth = dateFns.endOfMonth(date);
    const endWeekday = dateFns.getDay(endDateOfCurrentMonth);
    const gridDays = array.chunk([
        ...Array.from({
            length: startWeekday
        }, ()=>null),
        ...Array.from({
            length: daysInMonth
        }, (_, i)=>dateFns.setDate(date, i + 1)),
        ...Array.from({
            length: 6 - endWeekday
        }, ()=>null)
    ], 7);
    return gridDays;
};
/**
 * Generate day of the week columns names and abbreviations.
 *
 * @param date Date
 * @param locale Locale
 * @returns DatePickerColumn[]
 */ const generateColumns = (date, locale)=>{
    const start = dateFns.startOfWeek(date);
    return Array.from({
        length: 7
    }).fill(null).map((_, i)=>({
            abbr: dateFns.format(dateFns.addDays(start, i), 'EEEEEE', {
                locale
            }),
            title: dateFns.format(dateFns.addDays(start, i), 'EEEE', {
                locale
            })
        }));
};
/**
 * Get whether two dates are in the same day.
 *
 * @param date Date
 * @param other Date
 * @returns boolean
 */ const sameDay = (date, other)=>dateFns.isSameDay(date, other);
/**
 * Get whether two dates are in the same month.
 *
 * @param date Date
 * @param other Date
 * @returns boolean
 */ const sameMonth = (date, other)=>dateFns.isSameMonth(date, other);
/**
 * Get the start of the week for a date.
 *
 * @param date Date
 * @returns Date
 */ const startWeek = (date)=>dateFns.startOfWeek(date);
/**
 * Subtract a day from a date.
 *
 * @param date Date
 * @returns Date
 */ const subDay = (date)=>dateFns.subDays(date, 1);
/**
 * Subtract a month from a date.
 *
 * @param date Date
 * @returns Date
 */ const subMonth = (date)=>dateFns.subMonths(date, 1);
/**
 * Subtract a week from a date.
 *
 * @param date Date
 * @returns Date
 */ const subWeek = (date)=>dateFns.subWeeks(date, 1);
/**
 * Subtract a year from a date.
 *
 * @param date Date
 * @returns Date
 */ const subYear = (date)=>dateFns.subYears(date, 1);
/**
 * Get whether a date is today.
 *
 * @param date Date
 * @returns Date
 */ const todayDate = (date)=>dateFns.isToday(date);
/**
 * Get whether a date is valid. Handles both date objects and ISO-like date strings.
 *
 * @param date unknown
 * @returns boolean
 */ const validDate = (date, inputFormat)=>{
    if (!date) return false;
    if (typeof date === 'string' && validIso(date)) return dateFns.isValid(dateFns.parseISO(date));
    if (typeof date === 'string' && inputFormat) return dateFns.isValid(dateFns.parse(date, inputFormat, new Date()));
    return dateFns.isValid(date);
};
/**
 * Get whether a string is an ISO-like date (such as 2021-06-30 or 2021-06-30T00:00:00Z).
 * NOTE: This is not a comprehensive check and it doesn't validate the date itself.
 *
 * @param date string | null | undefined
 * @returns boolean
 */ const validIso = (date)=>{
    if (!date) return false;
    return date.split('-').length === 3;
};
/**
 * Validates a date object or date string.
 * If valid, returns valid as true as well as a date object, ISO-like date, and a formatted date value.
 * if invalid, returns valid as false.
 *
 * @param inputFormat DateInputFormats
 * @param maybeDate MaybeDate
 * @returns ValidatedDate
 */ const validateDate = (inputFormat, maybeDate)=>{
    const response = {
        date: new Date(),
        dateIso: '',
        dateValue: '',
        valid: true
    };
    if (maybeDate && validDate(maybeDate, inputFormat)) {
        const date = convertToDate(maybeDate, inputFormat);
        if (date) {
            const dateIso = dateFns.format(date, 'yyyy-MM-dd');
            const dateValue = dateFns.format(date, inputFormat);
            return {
                ...response,
                date,
                dateIso,
                dateValue
            };
        }
    }
    return {
        ...response,
        valid: false
    };
};

exports.addDay = addDay;
exports.addMonth = addMonth;
exports.addWeek = addWeek;
exports.addYear = addYear;
exports.beforeDate = beforeDate;
exports.convertToDate = convertToDate;
exports.endWeek = endWeek;
exports.formatDate = formatDate;
exports.generateCalendar = generateCalendar;
exports.generateColumns = generateColumns;
exports.sameDay = sameDay;
exports.sameMonth = sameMonth;
exports.startWeek = startWeek;
exports.subDay = subDay;
exports.subMonth = subMonth;
exports.subWeek = subWeek;
exports.subYear = subYear;
exports.todayDate = todayDate;
exports.validDate = validDate;
exports.validIso = validIso;
exports.validateDate = validateDate;
//# sourceMappingURL=date.js.map
